import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { EventPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { SchoolFilter, useSchoolFilterIds } from "@/components/school-filter";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { CalendarDays, Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { PinRowButton } from "@/components/pin-row-button";
import { SendMessageButton } from "@/components/send-message-button";
import { SchoolCrest } from "@/components/school-crest";

const typeBadge: Record<string, string> = {
  match_day: "rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  practice: "rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400",
  tryout: "rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
};

const courtNames = (e: EventPublic) =>
  e.courts ? [...e.courts].sort((a, b) => a.name.localeCompare(b.name)).map((c) => c.name).join(", ") : "";

export default function EventsGlobalPage() {
  const { user: authUser } = useAuth();
  const canManage = authUser?.roles.some((r) => r.role_type !== "super_parent" && r.role_type !== "parent" && r.role_type !== "player") ?? false;
  const navigate = useNavigate();
  const schoolIds = useSchoolFilterIds();
  const schoolRole = authUser?.roles.find(
    (r) => (r.role_type === "school_admin" || r.role_type === "coach" || r.role_type === "super_parent") && r.scope_type === "school",
  );
  const effectiveSchoolId = schoolIds[0] ?? schoolRole?.scope_id ?? "";
  const schoolIdsKey = schoolIds.join(",");

  const [typeFilter, setTypeFilter] = useState("all");
  const [whenFilter, setWhenFilter] = useState("upcoming");
  const [refreshTick, setRefreshTick] = useState(0);
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");

  // Redirect to calendar view when a single school is selected.
  useEffect(() => {
    if (schoolIds.length === 1) {
      navigate(`/schools/${schoolIds[0]}/calendar`, { replace: true });
    }
  }, [schoolIdsKey, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // The /events endpoint scopes + filters drafts post-fetch, so paginate the
  // (already-scoped) set client-side rather than risk wrong server-side counts.
  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: EventPublic[]; total: number }> => {
      const res = await api.getAllEvents({
        type: typeFilter !== "all" ? typeFilter : undefined,
        when: whenFilter,
        school_id: effectiveSchoolId || undefined,
      });
      const scoped = schoolIds.length > 1 ? res.events.filter((e) => schoolIds.includes(e.school_id)) : res.events;
      return applyClientListParams(scoped, params, {
        searchFields: (e) => [e.name, e.school_name],
        sortAccessors: {
          name: (e) => e.name,
          school: (e) => e.school_name,
          event_type: (e) => e.event_type,
          starts_at: (e) => e.starts_at,
        },
        defaultSort: "starts_at",
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [typeFilter, whenFilter, effectiveSchoolId, schoolIdsKey],
  );

  const fetchKey = [typeFilter, whenFilter, effectiveSchoolId, schoolIdsKey, refreshTick].join("|");

  async function handleDelete(id: string) {
    if (!confirm("Are you sure you want to delete this event?")) return;
    try {
      await api.deleteEvent(id);
      setRefreshTick((t) => t + 1);
    } catch {
      // surfaced by the table's next fetch
    }
  }

  const columns: DataTableColumn<EventPublic>[] = [
    { key: "name", header: "Name", sortable: true, className: "font-medium", render: (e) => e.name },
    {
      key: "school", header: "School", sortable: true,
      render: (e) => (
        <Link to={`/schools/${e.school_id}`} className="flex items-center gap-2 text-muted-foreground hover:underline" onClick={(ev) => ev.stopPropagation()}>
          <SchoolCrest name={e.school_name} logoUrl={e.school_logo_url} primaryColor={e.school_primary_color} size={22} />
          <span>{e.school_name}</span>
        </Link>
      ),
    },
    { key: "event_type", header: "Type", sortable: true, render: (e) => <span className={typeBadge[e.event_type] ?? ""}>{e.event_type.replace("_", " ")}</span> },
    { key: "court", header: "Court", className: "text-muted-foreground", render: courtNames },
    { key: "starts_at", header: "Date / Time", sortable: true, className: "text-muted-foreground", render: (e) => new Date(e.starts_at).toLocaleString() },
    { key: "matches", header: "Matches", className: "text-muted-foreground", render: (e) => e.match_count },
    {
      key: "actions", header: "", headerClassName: "w-36",
      render: (event) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <PinRowButton pinType="event" entityType="Event" entityId={event.id} />
          {event._actions.includes("send_message") && (
            <SendMessageButton contextType="event" contextId={event.id} contextLabel={event.name} />
          )}
          {event._actions.includes("view") && (
            <Link to={`/events/${event.id}`} className={buttonVariants({ variant: "ghost", size: "sm" })}>
              <Eye className="h-4 w-4" />
            </Link>
          )}
          {event._actions.includes("edit") && (
            <Link to={`/events/${event.id}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })}>
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {event._actions.includes("delete") && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(event.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6" data-page="events-global" data-state={tableState}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Events</h1>
          <p className="text-sm text-muted-foreground">All events across schools</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            {effectiveSchoolId ? (
              <>
                <Link to={`/schools/${effectiveSchoolId}/calendar`} className={buttonVariants({ size: "sm", variant: "outline" })}>
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Calendar
                </Link>
                <Link to={`/schools/${effectiveSchoolId}/events/new`} className={buttonVariants({ size: "sm" })}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Event
                </Link>
              </>
            ) : (
              <>
                <span className={buttonVariants({ size: "sm", variant: "outline" }) + " pointer-events-none opacity-50"} title="Select a school to view calendar">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Calendar
                </span>
                <span className={buttonVariants({ size: "sm", variant: "outline" }) + " pointer-events-none opacity-50"} title="Select a school to create an event">
                  <Plus className="mr-2 h-4 w-4" />
                  New Event
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-region="filters">
        <SchoolFilter />
        <select
          title="Event Type"
          aria-label="Filter by event type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="all">All Types</option>
          <option value="match_day">Match Days</option>
          <option value="practice">Practices</option>
          <option value="tryout">Tryouts</option>
        </select>
        <div className="flex gap-1">
          <Button variant={whenFilter === "upcoming" ? "default" : "outline"} size="sm" onClick={() => setWhenFilter("upcoming")}>Upcoming</Button>
          <Button variant={whenFilter === "past" ? "default" : "outline"} size="sm" onClick={() => setWhenFilter("past")}>Past</Button>
        </div>
      </div>

      <div data-region="results">
        <DataTable
          columns={columns}
          fetcher={fetcher}
          fetchKey={fetchKey}
          rowKey={(e) => e.id}
          rowProps={(e) => ({ "data-item": "event", "data-item-id": e.id })}
          searchPlaceholder="Search events, schools..."
          defaultSort="starts_at"
          defaultOrder={whenFilter === "past" ? "desc" : "asc"}
          emptyMessage={`No ${whenFilter} events found`}
          onStateChange={setTableState}
        />
      </div>
    </div>
  );
}
